import React from 'react';
import {
  Box, Typography, Divider, Button,
  alpha,
  TypographyProps,
  useTheme,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import {
  ExternalSubscriptionStatus,
  LoanRequest,
} from '../../types/authorization';
import LoanInfoRow, { parseAmountWithCurrency, parseCurrencyAbbreviation } from './LoanInfoRow';
import LoanInsuranceBreakdown from './LoanInsuranceBreakdown';
import bancameLogo from /* webpackPreload: true */ '../../assets/bancame_logo_dark.svg';
import AlertDialog from '../AlertDialog';
import PlanDetailInfo from '../PlanDetailInfo';
import analytics from '../../utils/analytics';
import LoanRequestInsurancesPreview from '../LoanRequestInsurancesPreview';
import { shouldShowMonthlyInsuranceCost } from '../LoanRequestInsurancesPreview/types';
import {
  monthlyInsuranceCostUf,
  totalInsuranceCostUf,
} from '../../utils/loanRequestInsuranceTotals';
import { ProductType } from '../../types/account';

interface LoanRequestConditionsProps {
  loanRequest: LoanRequest;
  onNext: () => void;
  externalSubscriptionStatus: ExternalSubscriptionStatus;
  productType: ProductType;
}

export default function LoanRequestConditions(props: LoanRequestConditionsProps) {
  const {
    loanRequest,
    onNext,
    externalSubscriptionStatus,
    productType,
  } = props;
  const theme = useTheme();
  const formatDate = (date: Date): string => new Date(date).toLocaleDateString('es-CL');

  const nextStep = externalSubscriptionStatus && ['PENDING', 'ACTIVE'].includes(externalSubscriptionStatus)
    ? 'CONTINUAR' : 'SUSCRIBIR PAC';

  const makeTypography = (label: string, color: string, variant: TypographyProps['variant']) => (
    <Typography variant={variant} color={color} component="span">
      {label}
    </Typography>
  );

  const nonTaxableInsurances = loanRequest.loanRequestInsurances
    .filter((insurance) => shouldShowMonthlyInsuranceCost(insurance.insurance));
  const taxableInsurances = loanRequest.loanRequestInsurances
    .filter((insurance) => !shouldShowMonthlyInsuranceCost(insurance.insurance));

  const creditCosts = loanRequest.bruteAmount - loanRequest.loanAmount;

  const isMortgage = productType === 'mortgage';
  const displayedInterestRate = isMortgage
    ? loanRequest.interestRate
    : loanRequest.interestRate / 12;
  const interestRateLabel = isMortgage ? 'Interés anual' : 'interés mensual';

  return (
    <Box sx={{
      mx: 'auto',
      bgcolor: 'white',
      borderRadius: 3,
      boxShadow: 1,
      padding: 4,
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      paddingBlock: { xs: 4, md: 6 },
      gap: { xs: 1, lg: 1.5, xl: 2 },
      backgroundColor: alpha(theme.palette.background.default, 0.5),
    }}
    >
      <Box sx={{
        display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2,
      }}
      >
        <object data={bancameLogo} width="140px" aria-labelledby="banca.me logo" />
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Box sx={{ textAlign: 'center', marginBottom: 2 }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom>Revisa tus condiciones</Typography>
        <Typography variant="body2" gutterBottom>
          Revisa las condiciones del crédito antes de continuar a
          {' '}
          <Typography variant="body2" fontWeight="bold" component="span" color="secondary.main">
            {nextStep === 'CONTINUAR' ? 'confirmar tu identidad y firmar el contrato.' : 'subscribir un pago automático de cuentas (PAC).'}
          </Typography>
        </Typography>
      </Box>

      <Divider sx={{ marginBlock: 2 }} />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <LoanInfoRow
          label="Monto solicitado"
          value={loanRequest.loanAmount}
          highlight
          currency={loanRequest.currency}
        />
        {creditCosts !== 0 ? (
          <Box sx={{
            display: 'flex', flexDirection: 'column',
          }}
          >
            <LoanInfoRow
              label="Gastos del crédito"
              value={creditCosts}
              currency={loanRequest.currency}
            />
            <LoanInfoRow
              label={makeTypography('• Impuestos', alpha('#000000', 0.65), 'caption')}
              value={loanRequest.taxes}
              size="small"
              color={alpha('#000000', 0.65)}
              currency={loanRequest.currency}
            />
            <LoanInfoRow
              label={makeTypography('• Costos de originación', alpha('#000000', 0.65), 'caption')}
              value={loanRequest.originCost}
              size="small"
              color={alpha('#000000', 0.65)}
              currency={loanRequest.currency}
            />
            {taxableInsurances.length > 0 ? (
              <>
                <LoanInfoRow
                  label={makeTypography('• Seguros asociados', alpha('#000000', 0.65), 'body2')}
                  value={loanRequest.insurance}
                  currency={loanRequest.currency}
                  color={alpha('#000000', 0.65)}
                  labelEndAdornment={(
                    <LoanRequestInsurancesPreview
                      hideIcons
                      hideTitle
                      periods={loanRequest.periods}
                      loanRequestInsurances={taxableInsurances}
                      currency={loanRequest.currency}
                      hideFooter
                      display="block"
                      pageCall={{
                        category: 'LR - CONDICIONES',
                        name: 'DETALLE SEGUROS',
                      }}
                      detailsCustomIcon={(
                        <InfoOutlinedIcon
                          style={{
                            fontSize: 12, marginBottom: 3, cursor: 'pointer', color: alpha('#000000', 0.65),
                          }}
                        />
                      )}
                    />
                  )}
                />
                <LoanInsuranceBreakdown
                  insurances={taxableInsurances}
                  periods={loanRequest.periods}
                />
              </>
            ) : null}
            {loanRequest.notary && loanRequest.notary !== 0 ? (
              <LoanInfoRow
                label={makeTypography('• Notario', alpha('#000000', 0.65), 'caption')}
                value={loanRequest.notary}
                size="small"
                currency={loanRequest.currency}
                color={alpha('#000000', 0.65)}
              />
            ) : null}
            {loanRequest.externalGuarantees && loanRequest.externalGuarantees !== 0 ? (
              <LoanInfoRow
                label={makeTypography('• Garantías asociadas', alpha('#000000', 0.65), 'caption')}
                value={loanRequest.externalGuarantees}
                size="small"
                currency={loanRequest.currency}
                color={alpha('#000000', 0.65)}
              />
            ) : null}
          </Box>
        ) : null}

      </Box>

      <Divider sx={{ marginBlock: 2 }} />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>

        <LoanInfoRow
          label="Monto bruto"
          value={loanRequest.bruteAmount}
          highlight
          currency={loanRequest.currency}
        />

        <Box sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', py: 1,
        }}
        >
          <Box>
            {productType !== 'mortgage' ? <Typography variant="body2">Interés total a pagar</Typography> : null}
            <Typography variant="body2" color={alpha('#000000', 0.65)}>
              {displayedInterestRate.toLocaleString('es-CL', {
                style: 'percent',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
              {' '}
              {interestRateLabel}
            </Typography>
            <Typography variant="body2" display="block" color={alpha('#000000', 0.65)}>
              {loanRequest.CAE.toLocaleString('es-CL', {
                style: 'percent',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
              {' '}
              CAE
            </Typography>
          </Box>
          {productType !== 'mortgage' ? (
            <Box sx={{
              textAlign: 'right',
              display: 'flex',
              width: '9em',
            }}
            >
              <Typography
                width="30%"
              >
                {parseCurrencyAbbreviation(loanRequest.currency)}
              </Typography>
              <Typography
                width="70%"
              >
                {parseAmountWithCurrency(
                  loanRequest.currency,
                  loanRequest.totalCost - loanRequest.bruteAmount
                  - totalInsuranceCostUf(loanRequest),
                  true,
                )}
              </Typography>
            </Box>
          ) : null}
        </Box>

      </Box>

      <Divider sx={{ marginBlock: 2 }} />

      <LoanInfoRow
        label="Costo total del crédito"
        value={loanRequest.totalCost}
        highlight
        color="primary.main"
        currency={loanRequest.currency}
      />

      <Divider sx={{ marginBlock: 2 }} />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        <LoanInfoRow
          label={`Valor cuota (${loanRequest.periods} ${loanRequest.periods > 1 ? 'meses' : 'mes'})`}
          value={loanRequest.installmentAmount}
          highlight
          color="primary.main"
          currency={loanRequest.currency}
        />
        {nonTaxableInsurances.length > 0 ? (
          <>
            <LoanInfoRow
              label={makeTypography('Seguros asociados', alpha('#000000', 0.65), 'body2')}
              value={monthlyInsuranceCostUf(loanRequest)}
              currency={loanRequest.currency}
              color={alpha('#000000', 0.65)}
              labelEndAdornment={(
                <LoanRequestInsurancesPreview
                  hideIcons
                  hideTitle
                  periods={loanRequest.periods}
                  loanRequestInsurances={nonTaxableInsurances}
                  currency={loanRequest.currency}
                  hideFooter
                  display="block"
                  pageCall={{
                    category: 'LR - CONDICIONES',
                    name: 'DETALLE SEGUROS',
                  }}
                  detailsCustomIcon={(
                    <InfoOutlinedIcon
                      style={{
                        fontSize: 12, marginBottom: 3, cursor: 'pointer', color: alpha('#000000', 0.65),
                      }}
                    />
                  )}
                />
              )}
            />
            <LoanInsuranceBreakdown
              insurances={nonTaxableInsurances}
              periods={loanRequest.periods}
            />
          </>
        ) : null}

        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          py: 1,
          px: 0,
        }}
        >
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            typography: 'caption',
            paddingLeft: 2,
            mt: 1,
          }}
          >
            <Typography variant="body2">
              {formatDate(loanRequest.expirationDate)}
            </Typography>
            <Typography variant="caption" display="block" fontStyle="italic">
              Primera cuota
            </Typography>
          </Box>

          <Box sx={{
            textAlign: 'right',
            width: '9em',
          }}
          >
            <AlertDialog
              button={(
                <Button
                  variant="text"
                  size="small"
                  sx={{
                    textDecoration: 'underline', mt: 0, width: 'max-content', paddingRight: 0,
                  }}
                >
                  Ver flujo de pago
                </Button>
              )}
              dialogContent={(
                <PlanDetailInfo
                  expirationDate={loanRequest.expirationDate}
                  installmentAmount={loanRequest.installmentAmount}
                  lastInstallmentAmount={loanRequest.lastInstallmentAmount}
                  periods={loanRequest.periods}
                  currency={loanRequest.currency}
                />
              )}
              hideSubmitButton
              centerButtons
              pageCall={{
                category: 'LR - CONDICIONES',
                name: 'FLUJO DE PAGO',
              }}
            />
          </Box>
        </Box>
      </Box>

      <Box sx={{
        mt: { xs: 6, md: 10 }, display: 'flex', justifyContent: 'center', alignItems: 'flex-end',
      }}
      >
        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={() => {
            analytics.track('Click Boton', {
              initiator: 'usuario',
              text: nextStep,
            });
            onNext();
          }}
        >
          {nextStep}
        </Button>
      </Box>

    </Box>
  );
}
